import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/lib/contact';

function SiteHeader() {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const getDashboardUrl = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin':
      case 'super_admin':
        return '/admin';
      case 'faculty':
        return '/faculty';
      default:
        return '/dashboard';
    }
  };

  return (
    <header className="w-full">
      {/* Top Bar - Contact info */}
      <div className="hidden md:block bg-white border-b border-warm-200">
        <div className="container-wide py-2.5 flex items-center justify-end text-sm text-warm-500">
          <div className="flex items-center gap-6">
            <a
              href="mailto:classes@stagedooraccess.com"
              className="flex items-center gap-2 hover:text-warm-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 512 512">
                <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z" />
              </svg>
              classes@stagedooraccess.com
            </a>
            <a
              href={`tel:${SUPPORT_PHONE_TEL}`}
              className="flex items-center gap-2 hover:text-warm-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 512 512">
                <path d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z" />
              </svg>
              {SUPPORT_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav className="bg-white border-b border-warm-200">
        <div className="container-wide py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src="/logo.png" alt="Stage Door Access" className="h-14" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-warm-700 hover:text-primary-600 transition-colors font-medium"
            >
              Home
            </Link>

            {isLoading ? (
              <span className="text-warm-400 text-sm">Loading...</span>
            ) : isAuthenticated ? (
              <>
                <span className="text-warm-500 text-sm hidden lg:block">
                  {user?.email}
                </span>
                <Link
                  to={getDashboardUrl()}
                  className="text-warm-700 hover:text-primary-600 transition-colors font-medium"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-warm-500 hover:text-warm-700 transition-colors font-medium"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="text-warm-700 hover:text-primary-600 transition-colors font-medium"
              >
                Sign In
              </Link>
            )}

          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 text-warm-700 hover:text-warm-900 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 left-0 bottom-0 w-72 bg-white z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-warm-200">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-warm-500 hover:text-warm-700 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Links */}
          <nav className="flex-1 overflow-y-auto py-4">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-6 py-3 text-warm-700 hover:bg-warm-50 hover:text-primary-600 font-medium transition-colors"
            >
              Home
            </Link>

            <div className="my-4 mx-6 border-t border-warm-200" />

            {isLoading ? (
              <span className="block px-6 py-3 text-warm-400 text-sm">Loading...</span>
            ) : isAuthenticated ? (
              <>
                <span className="block px-6 py-2 text-warm-500 text-sm">
                  {user?.email}
                </span>
                <Link
                  to={getDashboardUrl()}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-3 text-warm-700 hover:bg-warm-50 hover:text-primary-600 font-medium transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-6 py-3 text-warm-500 hover:bg-warm-50 hover:text-warm-700 font-medium transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-3 text-warm-700 hover:bg-warm-50 hover:text-primary-600 font-medium transition-colors"
                >
                  Sign In
                </Link>
                <div className="px-6 pt-2">
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="btn-primary w-full text-center text-sm"
                  >
                    Get Started
                  </Link>
                </div>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}

export default SiteHeader;
